// src/private/createPropagation.tsx
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo
} from "react";
import { useRefFrom } from "use-ref-from";

// src/private/createPropagateContextValue.tsx
function createPropagationContextValue() {
  const listeners = /* @__PURE__ */ new Set();
  return {
    addListener(listener) {
      listeners.add(listener);
    },
    removeListener(listener) {
      listeners.delete(listener);
    },
    runListeners(value) {
      listeners.forEach((listener) => listener(value));
    }
  };
}

// src/private/createPropagation.tsx
function createPropagation(init = {}) {
  const { allowPropagateDuringRender } = init;
  const context = createContext(createPropagationContextValue());
  let rendering = false;
  function PropagationScope({ children }) {
    const value = useMemo(createPropagationContextValue, []);
    return /* @__PURE__ */ React.createElement(context.Provider, { value }, children);
  }
  return {
    PropagationScope: memo(PropagationScope),
    useListen: (listener) => {
      const listenerRef = useRefFrom(listener);
      const { addListener, removeListener } = useContext(context);
      const wrappingListener = useMemo(() => {
        const wrappingListener2 = (value) => listenerRef.current(value);
        addListener(wrappingListener2);
        return wrappingListener2;
      }, [listenerRef]);
      useEffect(() => () => removeListener(wrappingListener), [wrappingListener]);
    },
    usePropagate: () => {
      rendering = true;
      const { runListeners } = useContext(context);
      useLayoutEffect(() => {
        rendering = false;
      });
      return useCallback(
        (value) => {
          if (rendering && !allowPropagateDuringRender) {
            return console.warn(
              "use-propagate: The propagate callback function should not be called while rendering, ignoring the call."
            );
          }
          runListeners(value);
        },
        [runListeners]
      );
    }
  };
}
export {
  createPropagation
};
//# sourceMappingURL=use-propagate.mjs.map